// IE Hub - Alert-specific Theme Tokens
// Default styling for alerts, notifications, and feedback components

export interface VariantTokens {
  fontWeight: number;
  borderRadius: number;
  padding: string;
  borderWidth?: number;
  borderStyle?: string;
}

export interface ColorTokens {
  backgroundColor?: string;
  color?: string;
  borderColor?: string;
}

export interface SeverityColorTokens {
  filled: ColorTokens;
  outlined: ColorTokens;
  standard: ColorTokens;
}

export interface SeverityTokens {
  colors: SeverityColorTokens;
  icon: string;
  autoHideDuration: number | null;
}

export interface PositionTokens {
  vertical: string;
  horizontal: string;
}

export interface SnackbarTokens {
  position: PositionTokens;
  maxStack: number;
  dense: boolean;
  preventDuplicate: boolean;
  autoHideDuration: number;
  TransitionComponent: string;
  anchorOrigin: PositionTokens;
}

export interface ToastContainerStyle {
  top: number;
  right: number;
  zIndex: number;
}

export interface ToastStyle {
  borderRadius: number;
  boxShadow: string;
  minWidth: number;
  maxWidth: number;
}

export interface ToastTokens {
  position: string;
  containerStyle: ToastContainerStyle;
  toastStyle: ToastStyle;
}

export interface LinearProgressTokens {
  height: number;
  borderRadius: number;
  backgroundColor: string;
}

export interface CircularProgressTokens {
  size: number;
  thickness: number;
}

export interface ProgressTokens {
  linear: LinearProgressTokens;
  circular: CircularProgressTokens;
}

export interface BackdropTokens {
  backgroundColor: string;
  zIndex: number;
}

export interface SpinnerTokens {
  size: number;
  color: string;
}

export interface SkeletonTokens {
  animation: string;
  variant: string;
  borderRadius: number;
}

export interface LoadingTokens {
  backdrop: BackdropTokens;
  spinner: SpinnerTokens;
  skeleton: SkeletonTokens;
}

export interface DialogPaperTokens {
  borderRadius: number;
  padding: number;
  maxWidth: number;
}

export interface DialogTitleTokens {
  fontSize: string;
  fontWeight: number;
  marginBottom: number;
}

export interface DialogContentTokens {
  fontSize: string;
  lineHeight: number;
  marginBottom: number;
}

export interface DialogActionsTokens {
  gap: number;
  justifyContent: string;
}

export interface DialogTokens {
  paper: DialogPaperTokens;
  title: DialogTitleTokens;
  content: DialogContentTokens;
  actions: DialogActionsTokens;
}

export interface BannerTokens {
  position: string;
  top: number;
  zIndex: number;
  width: string;
  borderRadius: number;
  boxShadow: string;
}

export interface ValidationTokens {
  fontSize: string;
  marginTop: number;
  color: string;
  display: string;
  alignItems: string;
  gap: number;
}

export interface AlertTokens {
  variants: {
    filled: VariantTokens;
    outlined: VariantTokens;
    standard: VariantTokens;
  };
  severity: {
    success: SeverityTokens;
    info: SeverityTokens;
    warning: SeverityTokens;
    error: SeverityTokens;
  };
  snackbar: SnackbarTokens;
  toast: ToastTokens;
  progress: ProgressTokens;
  loading: LoadingTokens;
  dialog: DialogTokens;
  banner: BannerTokens;
  validation: {
    error: ValidationTokens;
    success: ValidationTokens;
  };
}

export const alertTokens: AlertTokens = {
  // Alert variants
  variants: {
    filled: {
      fontWeight: 500,
      borderRadius: 8,
      padding: '12px 16px'
    },
    outlined: {
      fontWeight: 500,
      borderRadius: 8,
      padding: '12px 16px',
      borderWidth: 1,
      borderStyle: 'solid'
    },
    standard: {
      fontWeight: 500,
      borderRadius: 8,
      padding: '12px 16px'
    }
  },
  
  // Severity levels with colors and icons
  severity: {
    success: {
      colors: {
        filled: {
          backgroundColor: '#2e7d32',
          color: '#ffffff'
        },
        outlined: {
          borderColor: '#2e7d32',
          color: '#2e7d32',
          backgroundColor: 'rgba(46, 125, 50, 0.05)'
        },
        standard: {
          backgroundColor: 'rgba(46, 125, 50, 0.1)',
          color: '#1b5e20'
        }
      },
      icon: 'check_circle',
      autoHideDuration: 4000
    },
    
    info: {
      colors: {
        filled: {
          backgroundColor: '#0288d1',
          color: '#ffffff'
        },
        outlined: {
          borderColor: '#0288d1',
          color: '#0288d1',
          backgroundColor: 'rgba(2, 136, 209, 0.05)'
        },
        standard: {
          backgroundColor: 'rgba(2, 136, 209, 0.1)',
          color: '#01579b'
        }
      },
      icon: 'info',
      autoHideDuration: 6000
    },
    
    warning: {
      colors: {
        filled: {
          backgroundColor: '#ed6c02',
          color: '#ffffff'
        },
        outlined: {
          borderColor: '#ed6c02',
          color: '#ed6c02',
          backgroundColor: 'rgba(237, 108, 2, 0.05)'
        },
        standard: {
          backgroundColor: 'rgba(237, 108, 2, 0.1)',
          color: '#e65100'
        }
      },
      icon: 'warning',
      autoHideDuration: 8000
    },
    
    error: {
      colors: {
        filled: {
          backgroundColor: '#d32f2f',
          color: '#ffffff'
        },
        outlined: {
          borderColor: '#d32f2f',
          color: '#d32f2f',
          backgroundColor: 'rgba(211, 47, 47, 0.05)'
        },
        standard: {
          backgroundColor: 'rgba(211, 47, 47, 0.1)',
          color: '#c62828'
        }
      },
      icon: 'error',
      autoHideDuration: null // Persist until manually closed
    }
  },
  
  // Snackbar positioning and behavior
  snackbar: {
    position: {
      vertical: 'top',
      horizontal: 'right'
    },
    maxStack: 3,
    dense: false,
    preventDuplicate: true,
    autoHideDuration: 5000,
    TransitionComponent: 'Slide',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right'
    }
  },
  
  // Toast notifications
  toast: {
    position: 'top-right',
    containerStyle: {
      top: 24,
      right: 24,
      zIndex: 9999
    },
    toastStyle: {
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      minWidth: 300,
      maxWidth: 500
    }
  },
  
  // Progress indicators
  progress: {
    linear: {
      height: 4,
      borderRadius: 2,
      backgroundColor: 'rgba(0, 0, 0, 0.1)'
    },
    circular: {
      size: 24,
      thickness: 4
    }
  },
  
  // Loading states
  loading: {
    backdrop: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 9998
    },
    spinner: {
      size: 40,
      color: 'primary'
    },
    skeleton: {
      animation: 'wave',
      variant: 'rectangular',
      borderRadius: 8
    }
  },
  
  // Confirmation dialogs
  dialog: {
    paper: {
      borderRadius: 12,
      padding: 24,
      maxWidth: 400
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: 600,
      marginBottom: 16
    },
    content: {
      fontSize: '1rem',
      lineHeight: 1.5,
      marginBottom: 24
    },
    actions: {
      gap: 12,
      justifyContent: 'flex-end'
    }
  },
  
  // Banner alerts
  banner: {
    position: 'sticky',
    top: 0,
    zIndex: 1200,
    width: '100%',
    borderRadius: 0,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  
  // Inline validation
  validation: {
    error: {
      fontSize: '0.75rem',
      marginTop: 4,
      color: '#d32f2f',
      display: 'flex',
      alignItems: 'center',
      gap: 4
    },
    success: {
      fontSize: '0.75rem',
      marginTop: 4,
      color: '#2e7d32',
      display: 'flex',
      alignItems: 'center',
      gap: 4
    }
  }
};
